/* eslint-disable no-console */
import isEmpty from "lodash/isEmpty";
import { logAppInfo } from "@shared/utils/errors/errorLogger";
import type { StripeExpressCheckoutElement } from "@stripe/stripe-js";
import { useCallback, useEffect, useRef } from "react";
import { convertPriceStringToInteger } from "@shared/modules/financials/utils/price-utils";
import { PAYMENT_MANAGER_CONFIRMATION_STRATEGY } from "@shared/modules/payments/utils/manager/utils";

import { getCountryByName } from "@shared/lib/countries";
import { WalletElementSharedProps } from "../../components/WalletElement/WalletElement.props";
// eslint-disable-next-line import/no-cycle
import { usePaymentManager } from "../../contexts/PaymentManagerContext";
// eslint-disable-next-line import/no-cycle
import StripePaymentGateway from "../../gateways/StripePaymentGateway/StripePaymentGateway";
import { PurchaseData, PurchaseOptsType } from "../../types";
import StripeElement, {
  StripeElementProps,
} from "../StripeElement/StripeElement.web";
import { StripeWalletPaymentError } from "../../utils/stripe/errors/StripeWalletPaymentError";

/**
 * A containing element for Stripe.JS's Express Checkout element which displays virtual wallet payment options,
 * like Apple Pay, Google Pay, Paypal, Link, etc.
 * Wallet payment options are personal, and can be assumed that there will be no actors on behalf of another purchaser (ie. Box office)
 */
function StripeWalletElement({
  onElementReady,
  onElementError,
  onComponentUnmount,
  onPaymentSuccess,
  allowedShippingCountries,
}: StripeWalletElementProps) {
  const { manager } = usePaymentManager();

  const stabilizedOnElementReady = useCallback(onElementReady || (() => null), [
    onElementReady,
  ]);
  const stabilizedOnElementError = useCallback(onElementError || (() => null), [
    onElementError,
  ]);

  const stabilizedOnPaymentSuccess = useCallback(
    (purchaseData: PurchaseData) => onPaymentSuccess?.(purchaseData),
    [onPaymentSuccess]
  );

  const walletElementRef = useRef<StripeExpressCheckoutElement>();
  const walletElementClickHandlerTasks = useRef<Promise<unknown>>();

  /**
   * Asynchronously prepares the basket for a wallet payment
   */
  const prepareForWalletPayment = useCallback(
    async (isStripeWalletPayment: boolean) => {
      await manager.basketPaymentService.save();
      await manager.basketPaymentService.updateIsStripeWalletPayment(
        isStripeWalletPayment
      );
      await manager.basketPaymentService.extendBasket(isStripeWalletPayment);
      await manager.basketPaymentService.acceptTerms(isStripeWalletPayment);
    },
    [
      manager.basketPaymentService.save,
      manager.basketPaymentService.updateIsStripeWalletPayment,
      manager.basketPaymentService.extendBasket,
      manager.basketPaymentService.acceptTerms,
    ]
  );

  const handleElementCreated = useCallback(
    async (
      _element: Parameters<Required<StripeElementProps>["onElementCreate"]>[0]
    ) => {
      const element = _element as StripeExpressCheckoutElement;
      walletElementRef.current = element;

      /**
       * Call onElementReady when element is ready
       */
      element.on("ready", (e) => {
        console.log("Wallet element ready", e);
        logAppInfo("Wallet element ready", {
          category: "StripeWalletElement",
        });
        // On Stripe Elements, the element may be ready to be displayed but may not actually have any payment methods available.
        // Instead of solely relying on a ready event, we also look at the available payment methods to determine readiness
        if (e.availablePaymentMethods) {
          stabilizedOnElementReady();
        }
      });

      /**
       * Call onElementError when element has a loading error
       */
      element.once("loaderror", (error) => {
        console.error("Wallet element load error", error.error);
        logAppInfo(`Wallet element load error: ${error.error}`, {
          category: "StripeWalletElement",
        });
        stabilizedOnElementError();
      });

      /**
       * Handle a click event which fires when a user taps/clicks on the button.
       * The element works by firing a "click" event when a user interacts with the button,
       * which we need to respond by calling a `resolve` function within <=1s of the click event being fired.
       * This means that any asynchronous, "pre-purchase" logic that needs to happen must be done BEFORE a user is
       * able to click the Express Checkout button.
       *
       * This is the same story even with the old PaymentRequest button.
       */
      element.on("click", async (event) => {
        logAppInfo("Wallet element clicked", {
          category: "StripeWalletElement",
        });

        // Must be called within <=1s of the user clicking the element
        event.resolve();

        walletElementClickHandlerTasks.current = prepareForWalletPayment(true);
        await walletElementClickHandlerTasks.current;
        return walletElementClickHandlerTasks.current;
      });

      /**
       * Handle a cancel event which fires when a user dismisses the payment interface
       */
      element.on("cancel", async () => {
        logAppInfo("Wallet element cancelled", {
          category: "StripeWalletElement",
        });

        await walletElementClickHandlerTasks.current;
        await prepareForWalletPayment(false);
      });

      /**
       * Handle a confirm event which fires when customer finalizes their payment
       */
      element.on("confirm", async (event) => {
        logAppInfo("Wallet element confirmed", {
          category: "StripeWalletElement",
        });

        manager.onProcessing?.(true);
        await walletElementClickHandlerTasks.current;

        await manager.basketPaymentService.refresh();

        const purchaseOpts: PurchaseOptsType = {};

        // Update the shipping address with the latest data, if needed
        if (event.billingDetails && !isEmpty(event.billingDetails.address)) {
          logAppInfo("Updating billing address", {
            category: "StripeWalletElement",
          });

          const billingAddress = {
            street_address: event.billingDetails.address.line1,
            unit_number: event.billingDetails.address.line2 || "",
            city: event.billingDetails.address.city,
            country: event.billingDetails.address.country,
            postal: event.billingDetails.address.postal_code,
            province: event.billingDetails.address.state,
          };

          purchaseOpts.billing_address = billingAddress;
        }

        // Update the shipping address with the latest data, if needed
        if (event.shippingAddress && !isEmpty(event.shippingAddress.address)) {
          logAppInfo("Updating shipping address", {
            category: "StripeWalletElement",
          });

          const shippingAddress = {
            street_address: event.shippingAddress.address.line1,
            unit_number: event.shippingAddress.address.line2 || "",
            city: event.shippingAddress.address.city,
            country: event.shippingAddress.address.country,
            postal: event.shippingAddress.address.postal_code,
            province: event.shippingAddress.address.state,
          };

          purchaseOpts.shipping_address = shippingAddress;
        }

        try {
          const purchaseArtifacts = await manager.purchase(
            PAYMENT_MANAGER_CONFIRMATION_STRATEGY.WALLET,
            purchaseOpts
          );
          stabilizedOnPaymentSuccess?.(purchaseArtifacts);
        } catch (err) {
          // Specifically check for StripeWalletPaymentError. This happens in the StripePaymentGateway under confirmWalletPayment().
          if (err instanceof StripeWalletPaymentError) {
            event.paymentFailed({
              reason: "fail",
            });
          }

          manager.onProcessing?.(false);
        }
      });
    },
    [
      manager.handler,
      manager.basketPaymentService,
      manager.purchase,
      stabilizedOnElementError,
      stabilizedOnElementReady,
      prepareForWalletPayment,
      stabilizedOnPaymentSuccess,
    ]
  );

  /**
   * Call a callback function when the element unmounts
   */
  const staticOnComponentUnmount = useRef(onComponentUnmount);
  useEffect(() => {
    return () => {
      console.log("Unmounting wallet element");
      logAppInfo("Unmounting wallet element", {
        category: "StripeWalletElement",
      });
      // Detach listeners on unmount
      walletElementRef.current?.off("ready");
      walletElementRef.current?.off("loaderror");
      walletElementRef.current?.off("click");
      walletElementRef.current?.off("cancel");
      walletElementRef.current?.off("confirm");

      staticOnComponentUnmount.current?.();
    };
  }, []);

  if (!(manager.handler?.gateway instanceof StripePaymentGateway)) {
    console.log("No stripe payment gateway found");
    logAppInfo("No stripe payment gateway found", {
      category: "StripeWalletElement",
    });
    return null;
  }

  const shippingCountries =
    allowedShippingCountries && allowedShippingCountries?.length > 0
      ? allowedShippingCountries
          .map((country) => {
            const countryData = getCountryByName(country);
            return countryData?.countryShortCode;
          })
          .filter((country): country is string => country != null)
      : undefined;

  return (
    <StripeElement
      element="expressCheckout"
      elementProps={{
        paymentMethods: {
          amazonPay: "never",
          applePay: "always",
          googlePay: "always",
          link: "auto",
          paypal: "never",
        },
        buttonHeight: 40,
        buttonType: {
          applePay: "plain",
          googlePay: "plain",
        },
        emailRequired: true,
        phoneNumberRequired: true,
        // Pass shipping parameters depending on basket properties
        // @see https://docs.stripe.com/js/elements_object/create_express_checkout_element#express_checkout_element_create-options-shippingAddressRequired
        shippingAddressRequired: Boolean(
          manager.basket?.hasShippingAddressCollection
        ),
        shippingRates: manager.basket?.hasShippingAddressCollection
          ? [
              {
                id: "1",
                // The amount passed here will NOT be added to the amount that will be charged to the customer.
                // It is only used for UI purposes.
                amount: convertPriceStringToInteger(
                  manager.basket?.shippingCost ?? "0"
                ),
                displayName: `Standard shipping ${
                  convertPriceStringToInteger(
                    manager.basket?.shippingCost ?? "0"
                  ) > 0
                    ? "(included)"
                    : ""
                }`,
              },
            ]
          : [],
        allowedShippingCountries: shippingCountries,
      }}
      onElementCreate={handleElementCreated}
    />
  );
}

export default StripeWalletElement;

type StripeWalletElementProps = WalletElementSharedProps;
